import React, { createContext, useEffect, useMemo, useState } from "react";

export const CartContext = createContext(null);

const readStorage = (key, fallback) => {
  if (typeof window === "undefined") return fallback;

  try {
    const saved = window.localStorage.getItem(key);
    return saved ? JSON.parse(saved) : fallback;
  } catch {
    return fallback;
  }
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() =>
    readStorage("domtanion.cart", readStorage("ateliernoterman.cart", []))
  );
  const [wishlist, setWishlist] = useState(() =>
    readStorage("domtanion.wishlist", readStorage("ateliernoterman.wishlist", []))
  );

  useEffect(() => {
    try {
      window.localStorage.setItem("domtanion.cart", JSON.stringify(cartItems));
      window.localStorage.removeItem("ateliernoterman.cart");
    } catch { /* Browsing still works when storage is disabled. */ }
  }, [cartItems]);

  useEffect(() => {
    try {
      window.localStorage.setItem("domtanion.wishlist", JSON.stringify(wishlist));
      window.localStorage.removeItem("ateliernoterman.wishlist");
    } catch { /* Browsing still works when storage is disabled. */ }
  }, [wishlist]);

  const addToCart = (productId, quantity = 1, size = "", color = "") => {
    setCartItems((current) => {
      const matchingItem = current.find(
        (item) =>
          item.id === productId && item.size === size && item.color === color
      );

      if (!matchingItem) {
        return [...current, { id: productId, size, color, quantity }];
      }

      return current.map((item) =>
        item.id === productId && item.size === size && item.color === color
          ? { ...item, quantity: item.quantity + quantity }
          : item
      );
    });
  };

  const removeFromCart = (productId, size, color) => {
    setCartItems((current) =>
      current.filter(
        (item) =>
          !(item.id === productId && item.size === size && item.color === color)
      )
    );
  };

  const updateQuantity = (productId, size, color, quantity) => {
    if (quantity < 1) return;

    setCartItems((current) =>
      current.map((item) =>
        item.id === productId && item.size === size && item.color === color
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => setCartItems([]);

  const toggleWishlist = (productId) => {
    setWishlist((current) =>
      current.includes(productId)
        ? current.filter((id) => id !== productId)
        : [...current, productId]
    );
  };

  const cartCount = useMemo(
    () => cartItems.reduce((total, item) => total + item.quantity, 0),
    [cartItems]
  );

  const value = {
    cartItems,
    wishlist,
    cartCount,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    toggleWishlist,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
